import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

import {
  type AuthenticatedUser,
  changePassword as apiChangePassword,
  clearTokens,
  fetchMe,
  getAccessToken,
  hasPermission as _hasPermission,
  loginByNfcPin as apiLoginByNfcPin,
  loginByPassword as apiLoginByPassword,
  logout as apiLogout,
  refreshTokens,
} from "./auth";

// Sliding-session keepalive: while the tab is open, rotate the refresh
// token every PROACTIVE_REFRESH_MS so the access token never expires
// in the operator's hand AND the refresh token's expires_at keeps
// moving forward (backend rotates on every /auth/refresh).  The actual
// "you've been idle long enough — log back in" boundary is the backend
// REFRESH_TOKEN_TTL_SECONDS (3 days): when the tab is closed for that
// long without any refresh call, the stored refresh token expires.
const PROACTIVE_REFRESH_MS = 10 * 60 * 1000; // 10 minutes

interface AuthState {
  user: AuthenticatedUser | null;
  loading: boolean;
  loginByPassword: (login: string, password: string) => Promise<AuthenticatedUser>;
  loginByNfcPin: (nfcTag: string, pin: string) => Promise<AuthenticatedUser>;
  logout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  refresh: () => Promise<void>;
  hasPermission: (code: string) => boolean;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Bootstrap: if we have a token, try to fetch /me.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!getAccessToken()) {
        if (!cancelled) setLoading(false);
        return;
      }
      const me = await fetchMe();
      if (cancelled) return;
      if (me) setUser(me);
      else clearTokens();
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // React to token expiry from anywhere in the app.
  useEffect(() => {
    const onExpired = () => { setUser(null); };
    const onChange = async () => {
      if (!getAccessToken()) { setUser(null); return; }
      const me = await fetchMe();
      setUser(me);
    };
    window.addEventListener("mes:auth-expired", onExpired);
    window.addEventListener("mes:auth-change", onChange);
    return () => {
      window.removeEventListener("mes:auth-expired", onExpired);
      window.removeEventListener("mes:auth-change", onChange);
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!getAccessToken()) { setUser(null); return; }
    const me = await fetchMe();
    setUser(me);
  }, []);

  // Proactive silent refresh while the tab is open.  Without this,
  // an operator who walks away and comes back 30 minutes later hits a
  // dead access token on the next click; the api.ts retry kicks in,
  // but for slow networks that's an extra round-trip the operator
  // sees as a UI hitch.  Refreshing on a timer keeps the access token
  // live AND slides the refresh-token expiry forward.
  useEffect(() => {
    if (!user) return;
    const id = window.setInterval(() => {
      void refreshTokens();
    }, PROACTIVE_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [user]);

  const value: AuthState = {
    user,
    loading,
    async loginByPassword(login, password) {
      await apiLoginByPassword(login, password);
      const me = await fetchMe();
      if (!me) throw new Error("Login succeeded but /me failed");
      setUser(me);
      return me;
    },
    async loginByNfcPin(nfcTag, pin) {
      await apiLoginByNfcPin(nfcTag, pin);
      const me = await fetchMe();
      if (!me) throw new Error("Login succeeded but /me failed");
      setUser(me);
      return me;
    },
    async logout() {
      await apiLogout();
      setUser(null);
    },
    async changePassword(currentPassword, newPassword) {
      await apiChangePassword(currentPassword, newPassword);
      setUser(null);
    },
    refresh,
    hasPermission(code: string) {
      return _hasPermission(user, code);
    },
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
